import { createReducer, on } from '@ngrx/store';

import { ListActions } from './list.actions';
import { ListState } from './list.models';

export const initialListState: ListState = {
    listGuids: [],
    listNewGuids: [],
    selectedNodeUuid: '',
    nodesExpandState: {},
    isLoading: false,
};

export const listReducer = createReducer(
    initialListState,
    on(ListActions.setListGuids, (state, action): ListState => {
        return {
            ...state,
            listGuids: Array.from(action.listGuids),
            isLoading: false,
        };
    }),
    on(ListActions.navigateObject, (state, { nodeUuid }): ListState => {
        return { ...state, selectedNodeUuid: nodeUuid };
    }),
    on(ListActions.changeNodeExpandedState, (state, { nodeUuid, isExpanded }): ListState => {
        return {
            ...state,
            nodesExpandState: {
                ...state.nodesExpandState,
                [nodeUuid]: isExpanded,
            },
        };
    }),
    on(ListActions.changeAllNodesExpandedState, (state, { expanderNodes, isExpanded }): ListState => {
        const newExpandState = expanderNodes.reduce((acc, node) => {
            acc[node.id] = isExpanded;
            return acc;
        }, {});
        return {
            ...state,
            nodesExpandState: newExpandState,
        };
    }),
    on(ListActions.resetNodeExpandedState, (state): ListState => {
        return {
            ...state,
            nodesExpandState: {},
        };
    }),
    on(ListActions.addListGuids, (state, { listGuids }): ListState => {
        const newListGuids = new Set([...state.listGuids, ...listGuids]);
        return {
            ...state,
            listGuids: Array.from(newListGuids),
            isLoading: false,
        };
    }),
    on(ListActions.addNewListGuids, (state, { listGuids }): ListState => {
        if (!listGuids?.length) {
            return state;
        }
        // Keep the state reference stable when nothing is actually new, so the poll tick
        // doesn't invalidate every selector downstream of the list slice every 10 seconds.
        const existingGuids = new Set(state.listNewGuids);
        if (listGuids.every(guid => existingGuids.has(guid))) {
            return state;
        }
        listGuids.forEach(guid => existingGuids.add(guid));
        return {
            ...state,
            listNewGuids: Array.from(existingGuids),
        };
    }),
    on(ListActions.removeListGuids, (state, { listGuids }): ListState => {
        if (!listGuids || listGuids.length === 0) {
            return state;
        }
        const newListGuids = new Set(state.listGuids);
        listGuids.forEach(guid => newListGuids.delete(guid));
        return {
            ...state,
            listGuids: Array.from(newListGuids),
        };
    }),
    on(ListActions.clearNewListGuids, (state): ListState => {
        return {
            ...state,
            listNewGuids: [],
        };
    }),
    on(ListActions.setLoading, (state, { isLoading }): ListState => {
        return {
            ...state,
            isLoading,
        };
    })
);
